import { ChevronLeftIcon, Info } from "lucide-react";
import Link from "next/link";
import plur from "plur";
import type { ReactNode } from "react";

import {
  VIEW_MODES,
  type ViewMode,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/constants";
import { ViewModeToggle } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/view-mode-toggle";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { jobResultsHeaderTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";

export interface ResultsHeaderProps {
  totalResults: number;
  totalJobs: number;
  currentPage: number;
  totalPages: number;
  viewMode: ViewMode;
  getModeUrl: (mode: ViewMode) => string;
  hidePagination: boolean;
  initialContent: ReactNode;
  alertsSlot: ReactNode;
}

export function ResultsHeaderBackToListButton({
  getModeUrl,
}: {
  getModeUrl: (mode: ViewMode) => string;
}) {
  return (
    <Link href={getModeUrl(VIEW_MODES.list)}>
      <Button size="sm" variant="ghost">
        <ChevronLeftIcon />
        Back to list
      </Button>
    </Link>
  );
}

export function ResultsHeaderTotalResults({
  totalResults,
  totalJobs,
}: {
  totalResults: number;
  totalJobs: number;
}) {
  const hasSchoolCount = totalResults > 0;
  const hasJobCount = totalJobs > 0;
  const shouldShowUniqueJobsTooltip = hasJobCount && hasSchoolCount;

  if (!hasSchoolCount && !hasJobCount) {
    return null;
  }

  return (
    <span className="text-sm text-muted-foreground" data-testid="total-results">
      {hasJobCount && (
        <>
          <span className="font-semibold text-foreground">{totalJobs}</span>
          {` ${plur("Job", totalJobs)}`}
        </>
      )}
      {hasJobCount && hasSchoolCount && " | "}
      {hasSchoolCount && (
        <>
          <span className="font-semibold text-foreground">{totalResults}</span>
          {` ${plur("School", totalResults)}`}
        </>
      )}
      {shouldShowUniqueJobsTooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="How jobs are counted"
              className="ml-1 inline-flex size-4 shrink-0 items-center justify-center align-middle rounded-sm text-ink-faint transition-colors hover:text-ink-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-[250px]">
            Jobs number includes unique job postings. District-wide and pooled
            postings count only once, even if they appear across multiple
            schools.
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

export default function ResultsHeaderView({
  currentPage,
  totalPages,
  viewMode,
  getModeUrl,
  hidePagination,
  initialContent,
  alertsSlot,
}: ResultsHeaderProps): ReactNode {
  return (
    <>
      <div className="hidden lg:flex items-center justify-between">
        {initialContent}

        <div className="flex items-center gap-2">
          {hidePagination ? null : (
            <span
              className="text-xs text-ink-subtle"
              data-testid={
                jobResultsHeaderTestIds.RESULT_HEADER_PAGINATION_CONTAINER
              }
            >
              Page {currentPage} of {totalPages}
            </span>
          )}
          {alertsSlot}
          <ViewModeToggle
            viewMode={viewMode}
            getModeUrl={getModeUrl}
            listTestId={jobResultsHeaderTestIds.RESULT_HEADER_MODE_LIST}
            mapTestId={jobResultsHeaderTestIds.RESULT_HEADER_MODE_MAP}
          />
        </div>
      </div>
      <div className="hidden lg:flex border-b"></div>
    </>
  );
}
